import { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { BenefitUpdateFormProps } from "models/propTypes/benefitsPropTypes";
import { BenefitFormTypes } from "models/formTypes";
import {
  ChoiceProps,
  CurrencyInputProps,
  DateInputProps,
  MonthInputProps,
  MultiSearchInputProps,
  SearchInputProps,
  TextInputProps,
  ToggleProps,
  WintFormConnect,
} from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { CurrencyCode } from "models/enums/currency";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { BenefitsEnums } from "models/enums";
//#endregion
//#region components
import {
  Choice,
  CurrencyInput,
  DateInput,
  EditableTable,
  HorizontalFormField,
  MultiSearchInput,
  SearchInput,
  TextInput,
  Toggle,
  UserDisplay,
  useWintForm,
  VerticalFormField,
  WintForm,
} from "components/common/wintForms";
import BenefitValueDescription from "../_partials/benefitValueDescription";
import EndMonthInput from "../_partials/endMonthInput";
import { hasBenefitValue, isDeductionBenefitType, withDeductionWording } from "./useBenefitTypesHelper";
import MonthInput from "components/common/wintForms/formFields/monthInput";
import { FormExpansionPanel } from "components/common";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const BenefitUpdateForm: ForwardRefRenderFunction<FormHandles, BenefitUpdateFormProps> = (props, ref) => {
  const { defaultValues, handleSubmit, frequencyChoices, availableEmployees, validationSchema, readonly, onFormUpdate } =
    props;

  const initialBenefitValue = Number(defaultValues?.benefitValue);

  const { wintFormProps, connect, values } = useWintForm<BenefitFormTypes.BenefitUpdate>({
    formRef: ref,
    handleSubmit: handleSubmit,
    defaultValues: defaultValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    validateOnBlur: !readonly,
    validateOnChange: !readonly,
    onFormUpdate: onFormUpdate,
  });

  const employeeDetailsConnector = connect("employeeDetails");
  const benefitTypeId = values.benefitType?.id;

  const isDeduction = isDeductionBenefitType(benefitTypeId);

  const isCarBenefit =
    values.benefitType?.id === BenefitsEnums.BenefitType.Car ||
    values.benefitType?.id === BenefitsEnums.BenefitType.CarNetDeduction;

  const calculateEmployeeDetailsList = (
    persons: AutocompleteListItem[],
    existingEmployeeDetails: BenefitFormTypes.EmployeeDetails[]
  ) => {
    const employeeDetailsList: BenefitFormTypes.EmployeeDetails[] = [];

    for (const person of persons) {
      const personId = person.id ? Number(person.id) : undefined;
      const existingEmployeeDetail = existingEmployeeDetails.find((e) => e.user?.id === personId);
      if (existingEmployeeDetail) {
        employeeDetailsList.push(existingEmployeeDetail);
      } else {
        employeeDetailsList.push({
          user: { id: Number(person.id), name: person.value ?? "" },
          dateRange: { start: new Date(), end: undefined },
        });
      }
    }
    return employeeDetailsList;
  };

  const onEmployeesChange = (event: React.ChangeEvent<any>) => {
    const selectedEmployees = event.target.value as AutocompleteListItem[];
    employeeDetailsConnector.setValue(
      calculateEmployeeDetailsList(selectedEmployees, values.employeeDetails ?? []),
      true
    );
  };

  const expansionListTitle = `${LocalizationHelperInstance.getFormattedMessage(
    TranslationKeys.Benefit.EmployeesExpansionListTitle
  )} (${values.employeeDetails?.length ?? 0})`;

  return (
    <WintForm {...wintFormProps}>
      <VerticalFormField<SearchInputProps>
        Component={SearchInput}
        {...connect("benefitType")}
        label={TranslationKeys.Benefit.BenefitTypeLabel}
        items={[]}
        descriptionText={benefitTypeId !== undefined ? `Benefit.TypeDescription_${benefitTypeId}` : undefined}
        disabled
      />
      <VerticalFormField<TextInputProps>
        Component={TextInput}
        {...connect("name")}
        label={isDeduction ? TranslationKeys.Benefit.DeductionNameLabel : TranslationKeys.Benefit.NameLabel}
        disabled={readonly}
      />
      <VerticalFormField<MultiSearchInputProps>
        Component={MultiSearchInput}
        {...connect("employees")}
        label={TranslationKeys.Common.Employees}
        includeSelectAll
        selectAllIcon={IconDefinitionConstants.WUser}
        items={availableEmployees}
        onChange={onEmployeesChange}
        disabled={readonly || isCarBenefit}
      />
      <FormExpansionPanel title={expansionListTitle}>
        <EditableTable<BenefitFormTypes.EmployeeDetails>
          connect={connect as WintFormConnect<any>}
          defaultRow={{ user: undefined, dateRange: undefined }}
          {...employeeDetailsConnector}
          name="employeeDetails"
          value={values.employeeDetails ?? []}
          fields={{
            user: { Component: UserDisplay },
            dateRange: {
              Component: EndMonthInput,
              extraProps: {
                label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.EndDate),
                disabled: readonly,
              },
            },
          }}
          hideActions
          isCompactTable
          hideActionsCell
        />
      </FormExpansionPanel>
      <VerticalFormField<ChoiceProps>
        Component={Choice}
        {...connect("frequency")}
        label={TranslationKeys.Benefit.FrequencyLabel}
        items={isDeduction ? withDeductionWording(frequencyChoices) : frequencyChoices}
        showMainLabel
        disabled
      />
      {isCarBenefit && (
        <>
          <VerticalFormField<TextInputProps>
            Component={TextInput}
            {...connect("modelNumber")}
            label={TranslationKeys.Benefit.CarBenefitModelNumberLabel}
            disabled
          />
          <VerticalFormField<CurrencyInputProps>
            Component={CurrencyInput}
            {...connect("costOfExtraEquipment")}
            label={TranslationKeys.Benefit.CarBenefitCostOfExtraEquipmentLabel}
            decimalPoints={0}
            currency={CurrencyCode.SEK}
            disabled
          />
          <VerticalFormField<CurrencyInputProps>
            Component={CurrencyInput}
            {...connect("annualTax")}
            label={TranslationKeys.Benefit.CarBenefitAnnualTaxLabel}
            decimalPoints={0}
            currency={CurrencyCode.SEK}
            disabled
          />
          <HorizontalFormField<ToggleProps>
            Component={Toggle}
            {...connect("isAnnual10KmMileageMoreThan3000")}
            label={TranslationKeys.Benefit.CarBenefitNo10KmDrivenInServiceLabel}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Benefit.CarBenefitNo10KmDrivenInServiceDescription
            )}
            mainLabelGridSize={true}
            childGridSize={"auto"}
            disabled
          />
          <VerticalFormField<DateInputProps>
            Component={DateInput}
            {...connect("dateOfRegistration")}
            label={TranslationKeys.Benefit.CarBenefitDateOfRegistrationLabel}
            descriptionText={LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Benefit.CarBenefitDateOfRegistrationDescription
            )}
            disabled
          />
        </>
      )}
      <VerticalFormField<CurrencyInputProps>
        Component={CurrencyInput}
        {...connect("benefitValue")}
        label={isDeduction ? TranslationKeys.Benefit.DeductionAmountLabel : TranslationKeys.Benefit.BenefitValueLabel}
        decimalPoints={2}
        currency={CurrencyCode.SEK}
        descriptionText={hasBenefitValue(benefitTypeId) ? <BenefitValueDescription /> : undefined}
        showMainLabel
        disabled={readonly || isCarBenefit}
      />
      {Number(values.benefitValue) !== initialBenefitValue && (
        <VerticalFormField<MonthInputProps>
          Component={MonthInput}
          {...connect("benefitAmountEffectFrom")}
          label={TranslationKeys.Benefit.BenefitAmountEffectFromLabel}
          showMainLabel
          descriptionText={LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.Benefit.BenefitAmountEffectFromDescription
          )}
          minDate={new Date()}
          popperPositionFixed
        />
      )}
    </WintForm>
  );
};

export default forwardRef(BenefitUpdateForm);
